import { For, Show } from "solid-js";
import type { ParkingZone, Position } from "../../shared/api";
import { PIN_PARKING, PIN_VEHICLE_SELECTED, pinUrl } from "../map/pins";
import { fitStatic, staticMapUrl } from "../map/staticMap";
import { Button } from "./Button";
import { Modal } from "./Modal";

const W = 354;
const H = 196;

/**
 * Figma E2 (56:9495), without the free minutes (decision 14/10/2026): sliding to end outside a
 * parking zone first shows where the nearest one is (mini-map: the bike with its red pulse, the
 * zone, the route and its distance), then "Trouver un stationnement" (E3) or "Je suis bien
 * stationné(e)" (E4, photo proof).
 */
export function ParkingCheckModal(props: {
  bike: Position;
  zone: ParkingZone;
  /** The ride to the zone along the streets (straight line until it arrives). */
  path: Position[];
  distance: string;
  onFind: () => void;
  onParked: () => void;
  onClose: () => void;
}) {
  const token = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
  const area = () => props.zone.polygon?.map(([lng, lat]) => ({ lat, lng })) ?? [props.zone];
  const fit = () => fitStatic([props.bike, ...area(), ...props.path], W, H, 46);
  const pt = (p: Position) => fit().project(p);
  const mid = () => {
    const path = props.path.length > 1 ? props.path : [props.bike, props.zone];
    return pt(path[Math.floor(path.length / 2)] as Position);
  };
  const header = (
    <div class="absolute inset-0 overflow-hidden">
      <Show when={token}>
        {(t) => (
          <img
            src={staticMapUrl(fit(), W, H, t())}
            alt=""
            class="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </Show>
      <div class="absolute inset-0 bg-white/25" />
      <svg
        class="absolute inset-0"
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        aria-hidden="true"
      >
        {/* The zone: its polygon, else Figma's 104 × 82 rounded area. */}
        <Show
          when={props.zone.polygon && props.zone.polygon.length >= 3}
          fallback={
            <rect
              x={pt(props.zone).x - 52}
              y={pt(props.zone).y - 41}
              width="104"
              height="82"
              rx="18"
              fill="rgba(50,30,211,0.14)"
              stroke="#321ED3"
              stroke-width="2"
              stroke-dasharray="6 4"
            />
          }
        >
          <polygon
            points={area()
              .map((p) => `${pt(p).x},${pt(p).y}`)
              .join(" ")}
            fill="rgba(50,30,211,0.14)"
            stroke="#321ED3"
            stroke-width="2"
            stroke-dasharray="6 4"
            stroke-linejoin="round"
          />
        </Show>
        <polyline
          points={props.path.map((p) => `${pt(p).x},${pt(p).y}`).join(" ")}
          fill="none"
          stroke="#6D3E91"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-dasharray="0.1 9"
        />
      </svg>
      <img
        src={pinUrl(PIN_PARKING)}
        width={PIN_PARKING.size}
        height={PIN_PARKING.size}
        alt=""
        class="absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${pt(props.zone).x}px`, top: `${pt(props.zone).y}px` }}
      />
      {/* The bike, outside the zone: two red pulse rings behind the selected pin. */}
      <div
        class="absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${pt(props.bike).x}px`, top: `${pt(props.bike).y}px` }}
      >
        <For each={[92, 64]}>
          {(d, i) => (
            <span
              class="absolute rounded-full bg-[#E63836] animate-ping"
              style={{
                width: `${d}px`,
                height: `${d}px`,
                left: `${-d / 2}px`,
                top: `${-d / 2}px`,
                opacity: i() === 0 ? 0.14 : 0.24,
                "animation-duration": "1.8s",
              }}
            />
          )}
        </For>
        <img
          src={pinUrl(PIN_VEHICLE_SELECTED)}
          width={PIN_VEHICLE_SELECTED.size}
          height={PIN_VEHICLE_SELECTED.size}
          alt=""
          class="relative -translate-x-1/2 -translate-y-1/2"
        />
      </div>
      <span
        class="absolute flex h-[26px] -translate-x-1/2 -translate-y-1/2 items-center rounded-[13px] bg-white px-3 text-[13px] font-medium text-violet shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
        style={{ left: `${mid().x}px`, top: `${mid().y}px` }}
      >
        {props.distance}
      </span>
      <Show when={token}>
        <span class="absolute bottom-1 left-2 text-[8px] text-text/80">
          © Mapbox © OpenStreetMap
        </span>
      </Show>
    </div>
  );
  return (
    <Modal
      label="Pas dans une zone de stationnement"
      onClose={props.onClose}
      header={header}
      mapHeader
    >
      <h2 class="modal-title px-2 text-[20px]">
        Oups, vous n'êtes pas dans une zone de stationnement
      </h2>
      <p class="modal-text mt-[10px] px-3">
        Le stationnement le plus proche est à {props.distance}. Garez-vous dans la zone pour
        terminer votre trajet.
      </p>
      <Button class="mt-6" onClick={props.onFind}>
        Trouver un stationnement
      </Button>
      <Button variant="outline" class="mt-3" onClick={props.onParked}>
        Je suis bien stationné(e)
      </Button>
    </Modal>
  );
}
